import {
  Activity,
  AlertTriangle,
  BarChart,
  Bell,
  Calendar,
  CheckCircle,
  ClipboardList,
  CreditCardIcon,
  CurrencyIcon,
  FileText,
  Globe,
  // HelpCircle,
  Home,
  Lock,
  MapPin,
  Settings,
  Shield,
  User,
  UserMinus,
  Users,
} from 'lucide-react'

import { paths } from '@/config/paths'
import { PERMISSIONS } from '@/config/permissions'

import type { NavItem } from '@/types/nav-item'

/**
 * Unified Staff Navigation Configuration
 *
 * This navigation is shared by Super Admin, Gym Manager, and Assistant roles.
 * Menu items are filtered based on user permissions dynamically.
 *
 * Permission-based access control ensures:
 * - Super Admin sees all items
 * - Gym Manager sees management items
 * - Assistant sees operational items
 *
 * @param t Translation function from useTranslation hook
 */
export const getStaffNav = (t: (key: string, defaultValue: string) => string): NavItem[] => [
  {
    title: t('dashboard:nav.sidebar.dashboard', 'Dashboard'),
    url: paths.dashboard.root.getHref(),
    icon: Home,
    isActive: true,
    group: 'Main',
  },

  // Branches Module
  {
    title: t('dashboard:nav.sidebar.branches', 'Branches'),
    url: paths.dashboard.branches.root.getHref(),
    icon: MapPin,
    group: 'Main',
    requiredPermissions: [PERMISSIONS.BRANCHES.VIEW, PERMISSIONS.BRANCHES.EXPORT],
  },

  // Check-In Module (All staff have access)
  {
    title: t('dashboard:nav.sidebar.checkIn', 'Check In'),
    // Parent links to listing for consistency
    url: paths.dashboard.checkIns.root.getHref(),
    icon: CheckCircle,
    group: 'Main',
    requiredPermissions: [PERMISSIONS.CHECKINS.VIEW, PERMISSIONS.CHECKINS.EXPORT],
    items: [
      {
        title: t('dashboard:nav.sidebar.checkIn', 'Check In'),
        url: paths.dashboard.checkIn.root.getHref(),
        icon: CheckCircle,
        requiredPermissions: [PERMISSIONS.CHECKINS.CREATE],
      },
      {
        title: t('dashboard:nav.sidebar.checkIns', 'Check Ins'),
        url: paths.dashboard.checkIns.root.getHref(),
        icon: Calendar,
        requiredPermissions: [PERMISSIONS.CHECKINS.VIEW],
      },
    ],
  },

  // Classes Module
  {
    title: t('dashboard:nav.sidebar.classes', 'Classes'),
    url: paths.dashboard.classes.root.getHref(),
    icon: Calendar,
    group: 'Main',
    requiredPermissions: [PERMISSIONS.CLASSES.VIEW, PERMISSIONS.CLASSES.EXPORT],
    items: [
      {
        title: t('dashboard:nav.sidebar.classes', 'Classes'),
        url: paths.dashboard.classes.root.getHref(),
        icon: Calendar,
        requiredPermissions: [PERMISSIONS.CLASSES.VIEW, PERMISSIONS.CLASSES.EXPORT],
      },
      {
        title: t('dashboard:nav.sidebar.classTypes', 'Class Types'),
        url: paths.dashboard.classTypes.root.getHref(),
        icon: ClipboardList,
        requiredPermissions: [PERMISSIONS.CLASSES.VIEW, PERMISSIONS.CLASSES.EXPORT],
      },
      {
        title: t('dashboard:nav.sidebar.classTemplates', 'Class Templates'),
        url: paths.dashboard.classTemplates.root.getHref(),
        icon: FileText,
        requiredPermissions: [PERMISSIONS.CLASSES.VIEW, PERMISSIONS.CLASSES.EXPORT],
      },
    ],
  },

  // Bookings Module
  {
    title: t('dashboard:nav.sidebar.bookings', 'Bookings'),
    url: paths.dashboard.bookings.root.getHref(),
    icon: ClipboardList,
    group: 'Main',
    requiredPermissions: [PERMISSIONS.BOOKINGS.VIEW, PERMISSIONS.BOOKINGS.CANCEL], // bookings.export removed (undefined)
  },

  // Plans Module (Gym Manager & Super Admin only)
  {
    title: t('dashboard:nav.sidebar.plans', 'Plans'),
    url: paths.dashboard.plans.root.getHref(),
    icon: CurrencyIcon,
    group: 'Main',
    requiredPermissions: [PERMISSIONS.PLANS.VIEW, PERMISSIONS.PLANS.EXPORT],
  },

  // Invoices/Payments Module (Gym Manager & Super Admin only)
  {
    title: t('dashboard:nav.sidebar.invoices', 'Invoices'),
    url: paths.dashboard.invoices.root.getHref(),
    icon: CreditCardIcon,
    group: 'Main',
    requiredPermissions: [PERMISSIONS.PAYMENTS.VIEW, PERMISSIONS.PAYMENTS.EXPORT],
  },
  // Members Module
  {
    title: t('dashboard:nav.sidebar.members', 'Members'),
    url: paths.dashboard.members.root.getHref(),
    icon: User,
    group: 'Management',
    requiredPermissions: [PERMISSIONS.MEMBERS.VIEW, PERMISSIONS.MEMBERS.EXPORT],
  },

  // Staff Management (Gym Manager & Super Admin only)
  {
    title: t('dashboard:nav.sidebar.staff', 'Staff'),
    // Link to first child route
    url: paths.dashboard.assistants.root.getHref(),
    icon: Users,
    group: 'Management',
    requiredPermissions: [PERMISSIONS.STAFF.VIEW, PERMISSIONS.COACHES.VIEW],
    items: [
      {
        title: t('dashboard:nav.sidebar.assistants', 'Assistants'),
        url: paths.dashboard.assistants.root.getHref(),
        icon: User,
        requiredPermissions: [PERMISSIONS.STAFF.VIEW],
      },
      {
        title: t('dashboard:nav.sidebar.gymManagers', 'Gym Managers'),
        url: paths.dashboard.gymManagers.root.getHref(),
        icon: Users,
        requiredPermissions: [PERMISSIONS.STAFF.VIEW],
      },
      {
        title: t('dashboard:nav.sidebar.coaches', 'Coaches'),
        url: paths.dashboard.coaches.root.getHref(),
        icon: Activity,
        requiredPermissions: [PERMISSIONS.COACHES.VIEW],
      },
    ],
  },

  // Member Moderation (Gym Manager & Super Admin only)
  {
    title: t('dashboard:nav.sidebar.moderation', 'Moderation'),
    url: paths.dashboard.bannedUsers.root.getHref(),
    icon: Shield,
    group: 'Management',
    requiredPermissions: [PERMISSIONS.MEMBERS.BAN, PERMISSIONS.MEMBERS.DELETE],
    items: [
      {
        title: t('dashboard:nav.sidebar.bannedUsers', 'Banned Users'),
        url: paths.dashboard.bannedUsers.root.getHref(),
        icon: UserMinus,
        requiredPermissions: [PERMISSIONS.MEMBERS.BAN],
      },
      {
        title: t('dashboard:nav.sidebar.deletionRequests', 'Deletion Requests'),
        url: paths.dashboard.deletionRequests.root.getHref(),
        icon: AlertTriangle,
        requiredPermissions: [PERMISSIONS.MEMBERS.DELETE],
      },
    ],
  },

  // Reports Module (Gym Manager & Super Admin only)
  {
    title: t('dashboard:nav.sidebar.reports', 'Reports'),
    url: paths.dashboard.reports.root.getHref(),
    icon: FileText,
    group: 'Reports',
    requiredPermissions: [
      PERMISSIONS.REPORTS.MEMBERSHIP,
      PERMISSIONS.REPORTS.BOOKINGS,
      PERMISSIONS.REPORTS.FINANCES,
      PERMISSIONS.REPORTS.ATTENDANCE,
    ],
    items: [
      {
        title: t('dashboard:nav.sidebar.attendanceReports', 'Attendance Reports'),
        url: paths.dashboard.reports.attendance.getHref(),
        icon: Activity,
        requiredPermissions: [PERMISSIONS.REPORTS.ATTENDANCE],
      },
      {
        title: t('dashboard:nav.sidebar.membershipReports', 'Membership Reports'),
        url: paths.dashboard.reports.membership.getHref(),
        icon: User,
        requiredPermissions: [PERMISSIONS.REPORTS.MEMBERSHIP],
      },
      {
        title: t('dashboard:nav.sidebar.bookingReports', 'Booking Reports'),
        url: paths.dashboard.reports.bookings.getHref(),
        icon: Calendar,
        requiredPermissions: [PERMISSIONS.REPORTS.BOOKINGS],
      },
      {
        title: t('dashboard:nav.sidebar.financialReports', 'Financial Reports'),
        url: paths.dashboard.reports.financial.getHref(),
        icon: CurrencyIcon,
        requiredPermissions: [PERMISSIONS.REPORTS.FINANCES],
      },
    ],
  },

  // System Administration (Super Admin only)
  {
    title: t('dashboard:nav.sidebar.system', 'System'),
    url: paths.dashboard.settings.root.getHref(),
    icon: Settings,
    group: 'Administration',
    requiredPermissions: [PERMISSIONS.SETTINGS.VIEW],
    items: [
      {
        title: t('dashboard:nav.sidebar.rolesPermissions', 'Roles & Permissions'),
        url: paths.dashboard.roles.permissions.getHref(),
        icon: Shield,
        requiredPermissions: [PERMISSIONS.SETTINGS.VIEW],
      },
      {
        title: t('dashboard:nav.sidebar.appSettings', 'App Settings'),
        url: paths.dashboard.settings.root.getHref(),
        icon: Settings,
        requiredPermissions: [PERMISSIONS.SETTINGS.VIEW],
      },
      {
        title: t('dashboard:nav.sidebar.auditLogs', 'Audit Logs'),
        url: paths.dashboard.auditLogs.root.getHref(),
        icon: FileText,
        requiredPermissions: [PERMISSIONS.SETTINGS.VIEW],
      },
      {
        title: t('dashboard:nav.sidebar.apiHealth', 'API Health'),
        url: paths.dashboard.apiHealth.root.getHref(),
        icon: BarChart,
        requiredPermissions: [PERMISSIONS.SETTINGS.VIEW],
      },
    ],
  },

  // Profile Settings (All staff)
  {
    title: t('dashboard:nav.sidebar.profileSettings', 'Profile Settings'),
    url: paths.dashboard.profile.root.getHref(),
    icon: Settings,
    group: 'Settings',
    items: [
      { title: t('dashboard:nav.sidebar.general', 'General'), url: paths.dashboard.profile.root.getHref(), icon: User },
      {
        title: t('dashboard:nav.sidebar.security', 'Security'),
        url: paths.dashboard.profile.security.getHref(),
        icon: Lock,
      },
      {
        title: t('dashboard:nav.sidebar.localization', 'Localization'),
        url: paths.dashboard.profile.localization.getHref(),
        icon: Globe,
      },
      {
        title: t('dashboard:nav.sidebar.notifications', 'Notifications'),
        url: paths.dashboard.profile.notifications.getHref(),
        icon: Bell,
      },
    ],
  },

  // Help (Super Admin only)
  // {
  //   title: t('dashboard:nav.sidebar.help', 'Help'),
  //   url: paths.dashboard.support.help.getHref(),
  //   icon: HelpCircle,
  //   group: 'Settings',
  //   requiredPermissions: [PERMISSIONS.SETTINGS.VIEW],
  // },
]
